import { useCallback, useEffect, useRef } from 'react';

import { getSelectionSignature } from './evaluatorSelection';

import type { SelectionContent } from './evaluatorSelection';
import type {
    CycleEvaluatorCriteria,
    CycleEvaluatorSelection,
} from '@/types/cycle-evaluation/CycleEvaluatorSelection';

import { useUpdateCycleEvaluatorSelection } from '@/api/cycle-evaluation-services/cycle-evaluator-selection';
import { useDebounce } from '@/hooks/useDebounce';

const AUTOSAVE_DELAY_MS = 500;

interface UseAutosaveEvaluatorSelectionParams {
    isReadOnly: boolean;
    isHydrated: boolean;
    hasUserEdited: boolean;
    cycleEvaluationId: string | undefined;
    storedSelection: CycleEvaluatorSelection | undefined;
    cycleEvaluatorCriterias: CycleEvaluatorCriteria[];
    suggestedReviewerCount: number;
}

const useAutosaveEvaluatorSelection = ({
    isReadOnly,
    isHydrated,
    hasUserEdited,
    cycleEvaluationId,
    storedSelection,
    cycleEvaluatorCriterias,
    suggestedReviewerCount,
}: UseAutosaveEvaluatorSelectionParams) => {
    const { mutateAsync: updateCycleEvaluatorSelection } = useUpdateCycleEvaluatorSelection();

    // No signature before hydration: an empty seed makes an emptied draft read as unchanged.
    const debouncedSignature = useDebounce(
        isHydrated
            ? getSelectionSignature({ cycleEvaluatorCriterias, suggestedReviewerCount })
            : undefined,
        AUTOSAVE_DELAY_MS,
    );

    const savedSignatureRef = useRef<string | undefined>(undefined);

    const latestRef = useRef({
        isReadOnly,
        isHydrated,
        hasUserEdited,
        cycleEvaluationId,
        storedSelection,
        cycleEvaluatorCriterias,
        suggestedReviewerCount,
        updateCycleEvaluatorSelection,
    });
    latestRef.current = {
        isReadOnly,
        isHydrated,
        hasUserEdited,
        cycleEvaluationId,
        storedSelection,
        cycleEvaluatorCriterias,
        suggestedReviewerCount,
        updateCycleEvaluatorSelection,
    };

    // Next replaces the whole object too, so a pending write here would land after it and undo it.
    const cancelPendingAutosave = useCallback((selection: SelectionContent) => {
        savedSignatureRef.current = getSelectionSignature(selection);
    }, []);

    const restorePendingAutosave = useCallback(() => {
        savedSignatureRef.current = undefined;
    }, []);

    useEffect(() => {
        const {
            isReadOnly: readOnly,
            isHydrated: hydrated,
            hasUserEdited: edited,
            cycleEvaluationId: evaluationId,
            storedSelection: stored,
            cycleEvaluatorCriterias: criterias,
            suggestedReviewerCount: reviewerCount,
            updateCycleEvaluatorSelection: save,
        } = latestRef.current;

        if (!edited || readOnly || !hydrated || !evaluationId || !stored) {
            return;
        }

        const draftSelection: SelectionContent = {
            cycleEvaluatorCriterias: criterias,
            suggestedReviewerCount: reviewerCount,
        };

        if ((savedSignatureRef.current ?? getSelectionSignature(stored)) === debouncedSignature) {
            return;
        }

        // A refused autosave stays silent and stays unsaved — Next reports the failure.
        void save({ id: stored.id, cycleEvaluationId: evaluationId, ...draftSelection })
            .then(() => {
                savedSignatureRef.current = getSelectionSignature(draftSelection);
            })
            .catch(() => undefined);
    }, [debouncedSignature]);

    return { cancelPendingAutosave, restorePendingAutosave };
};

export { useAutosaveEvaluatorSelection, AUTOSAVE_DELAY_MS };
